import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import useCart from 'hooks/use-cart';
import { colors } from 'shared/styles';
import type { RewardV2 } from 'src/gql/graphql';
import Button from '../../core/button';

type RewardsProps = {
  rewards: Array<RewardV2>;
  onApplyReward?: (costV2: number, copy: string, id: string) => void;
  onUndoReward?: () => void;
  rewardsBalanceNumber: number;
  showDiscountRewardsOnly: boolean;
};

export const DposApplyRewardsCheckout = ({
  rewards = [],
  onApplyReward,
  onUndoReward,
  rewardsBalanceNumber,
  showDiscountRewardsOnly,
}: RewardsProps): JSX.Element => {
  const Cart = useCart();
  const { appliedRewards } = Cart;
  const availableRewards = rewards?.filter(({ available }) => available);
  const [appliedRewardId, setAppliedRewardId] = useState<string | null>(null);

  const handleApplyReward = (costV2: number, copy: string, id: string): void => {
    if (onApplyReward) {
      onApplyReward(costV2, copy, id);
      setAppliedRewardId(id);
    }
  };

  const handleUndoReward = (): void => {
    if (onUndoReward) {
      onUndoReward();
      setAppliedRewardId(null);
    }
  };

  useEffect(() => {
    if (appliedRewardId && !appliedRewards.some((reward) => reward.id === appliedRewardId)) {
      setAppliedRewardId(null);
    }
  }, [appliedRewards, appliedRewardId]);

  return (
    <Container>
      {showDiscountRewardsOnly ? (
        <InStoreHeaderText className='fine-print'>
          <b>Redeemable in-store only.</b> Cannot be used online.
        </InStoreHeaderText>
      ) : (
        <HeaderText>The following reward options are eligible for this order</HeaderText>
      )}

      {availableRewards.length > 0 ? (
        availableRewards.map(({ copy, costV2, id, isRedeemableOnline, isEligible }) => {
          const isApplied = appliedRewardId === id;
          const isEligibleForReward = isEligible !== false;
          const isDisabled =
            (appliedRewardId !== null && !isApplied) ||
            !isRedeemableOnline ||
            !isEligibleForReward ||
            showDiscountRewardsOnly;
          const tooExpensive = costV2 > rewardsBalanceNumber;

          return (
            <RewardCard
              key={`${id}-${copy ?? ''}`}
              isDisabled={isDisabled}
              hide={!isEligibleForReward}
              showDiscountRewardsOnly={showDiscountRewardsOnly}
            >
              <RewardInfo>
                <RewardTitle isDisabled={isDisabled || tooExpensive}>{copy}</RewardTitle>
                <PointsRow>
                  <PointsCost isDisabled={isDisabled || tooExpensive}>{costV2} pts</PointsCost>
                  {isApplied && (
                    <AppliedContainer>
                      <CheckIcon>✓</CheckIcon>
                      <AppliedText>Applied</AppliedText>
                    </AppliedContainer>
                  )}
                </PointsRow>
              </RewardInfo>
              {!showDiscountRewardsOnly ? (
                <>
                  {isApplied ? (
                    <Button onClick={handleUndoReward}>UNDO</Button>
                  ) : (
                    <Button
                      onClick={() => handleApplyReward(costV2, copy ?? '', id)}
                      disabled={isDisabled || tooExpensive}
                    >
                      APPLY
                    </Button>
                  )}
                </>
              ) : null}
            </RewardCard>
          );
        })
      ) : (
        <NoRewardsAvailable>No eligible rewards at this time</NoRewardsAvailable>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HeaderText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 8px 0;
  font-weight: 400;
`;

const InStoreHeaderText = styled.p`
  font-size: 16px;
  color: #6b7280;
  font-weight: 400;
`;

const RewardCard = styled.div<{ isDisabled?: boolean; hide?: boolean; showDiscountRewardsOnly?: boolean }>`
  display: ${({ hide }) => (hide ? 'none' : 'flex')};

  background-color: transparent;
  padding: ${({ showDiscountRewardsOnly }) => (showDiscountRewardsOnly ? '0' : '12px 0')};
  justify-content: space-between;
  align-items: center;
`;

const RewardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const RewardTitle = styled.h3<{ isDisabled?: boolean }>`
  font-size: 16px;
  font-weight: 600;
  color: ${({ isDisabled }) => (isDisabled ? '#A0A7AB' : '#1f2937')};
  margin: 0;
  line-height: 1.25;
`;

const PointsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AppliedContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CheckIcon = styled.span`
  color: #10b981;
  font-weight: bold;
  font-size: 14px;
`;

const AppliedText = styled.span`
  color: #10b981;
  font-size: 14px;
  font-weight: 500;
`;

const PointsCost = styled.p<{ isDisabled?: boolean }>`
  font-size: 14px;
  color: ${({ isDisabled }) => (isDisabled ? '#A0A7AB' : '#6b7280')};
  margin: 0;
  font-weight: 400;
`;

const NoRewardsAvailable = styled.div`
  color: ${colors.blueGrey};
  text-align: center;
  padding: 24px;
  font-style: italic;
`;
